// Reads and removes the metadata a camera embeds in a photo, on the bytes alone —
// no decoding, so nothing here costs image quality.
//
// The server does the same job for what is already in the bucket
// (Tribute.Engines.ImageMetadataEngine), but an upload has to be cleaned before it
// is stored: the CDN caches a rendition for a year the first time anyone asks for
// one, so a photo uploaded sideways stays sideways long after the object behind it
// has been fixed.

const ORIENTATION_TAG = 0x0112;
const EXIF_MARKER = 0xe1;

// Blocks that describe the photographer rather than the picture: EXIF (which holds
// the GPS fix, camera and timestamps), Photoshop/IPTC, and free-text comments.
// JFIF, ICC and Adobe blocks are deliberately absent -- those tell a decoder how to
// render the image, and dropping the colour profile visibly shifts a photo taken in
// Display P3.
const PERSONAL_MARKERS = new Set([EXIF_MARKER, 0xed, 0xfe]);

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];
const PNG_METADATA_CHUNKS = new Set(["eXIf", "tEXt", "zTXt", "iTXt", "tIME"]);

export function isJpeg(bytes) {
    return bytes.length > 3 && bytes[0] === 0xff && bytes[1] === 0xd8;
}

export function isPng(bytes) {
    return bytes.length > 8 && PNG_SIGNATURE.every((byte, index) => bytes[index] === byte);
}

/**
 * The EXIF orientation a JPEG asks to be displayed at, 1..8, or null when it
 * carries none or cannot be parsed. Null means "leave the pixels alone".
 */
export function readOrientation(bytes) {
    for (const segment of segments(bytes)) {
        if (segment.marker !== EXIF_MARKER) continue;

        const orientation = orientationFromExif(bytes, segment);
        if (orientation) return orientation;
    }

    return null;
}

/**
 * The same JPEG with its EXIF block removed, for handing to a decoder. Browsers
 * disagree about whether they auto-orient, and one that does would turn a photo a
 * second time on top of the turn we are about to apply ourselves.
 */
export function stripExif(bytes) {
    return stripMarkers(bytes, new Set([EXIF_MARKER]));
}

/** The same JPEG with every block describing the photographer removed. */
export function stripPersonalMetadata(bytes) {
    return stripMarkers(bytes, PERSONAL_MARKERS);
}

/** The same PNG with every metadata-bearing chunk removed. */
export function stripPngMetadata(bytes) {
    if (!isPng(bytes)) return bytes;

    const kept = [bytes.subarray(0, 8)];
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    let cursor = 8;
    let removed = false;

    while (cursor + 8 <= bytes.length) {
        const size = view.getUint32(cursor);
        const end = cursor + 12 + size;
        // Rewriting a file we could not read end to end risks uploading a shorter one.
        if (end > bytes.length) return bytes;

        const type = String.fromCharCode(...bytes.subarray(cursor + 4, cursor + 8));
        if (PNG_METADATA_CHUNKS.has(type)) removed = true;
        else kept.push(bytes.subarray(cursor, end));

        cursor = end;
    }

    return removed ? concat(kept) : bytes;
}

// Walks the marker segments in front of the compressed image data, which is where
// everything we care about lives.
function* segments(bytes) {
    if (!isJpeg(bytes)) return;

    let cursor = 2;
    while (cursor + 3 < bytes.length) {
        if (bytes[cursor] !== 0xff) return;

        const marker = bytes[cursor + 1];
        // A padding byte before the next marker, which is legal and which phones emit.
        if (marker === 0xff) {
            cursor += 1;
            continue;
        }
        // End of image, or the start of the scan data we never need to read past.
        if (marker === 0xd9 || marker === 0xda) return;

        const length = (bytes[cursor + 2] << 8) | bytes[cursor + 3];
        if (length < 2 || cursor + 2 + length > bytes.length) return;

        yield { marker, start: cursor, payload: cursor + 4, end: cursor + 2 + length };
        cursor += 2 + length;
    }
}

function orientationFromExif(bytes, segment) {
    // A DataView throws rather than reading past the end, which is exactly the
    // answer we want for a malformed header: report nothing and rotate nothing.
    try {
        if (String.fromCharCode(...bytes.subarray(segment.payload, segment.payload + 4)) !== "Exif") return null;

        const tiff = segment.payload + 6;
        const view = new DataView(bytes.buffer, bytes.byteOffset + tiff, segment.end - tiff);

        // Apple writes little-endian TIFF headers, Canon and Nikon big-endian.
        const marker = view.getUint16(0);
        if (marker !== 0x4949 && marker !== 0x4d4d) return null;
        const little = marker === 0x4949;

        if (view.getUint16(2, little) !== 0x2a) return null;

        const directory = view.getUint32(4, little);
        const entries = view.getUint16(directory, little);

        for (let index = 0; index < entries; index++) {
            const entry = directory + 2 + index * 12;
            if (view.getUint16(entry, little) !== ORIENTATION_TAG) continue;

            const orientation = view.getUint16(entry + 8, little);
            // Anything outside the range means we have misread the file, and turning
            // on it would spin a photo that was already fine.
            return orientation >= 1 && orientation <= 8 ? orientation : null;
        }

        return null;
    } catch {
        return null;
    }
}

function stripMarkers(bytes, markers) {
    if (!isJpeg(bytes)) return bytes;

    const kept = [];
    let cursor = 0;
    let removed = false;

    for (const segment of segments(bytes)) {
        if (!markers.has(segment.marker)) continue;

        kept.push(bytes.subarray(cursor, segment.start));
        cursor = segment.end;
        removed = true;
    }

    if (!removed) return bytes;

    kept.push(bytes.subarray(cursor));
    return concat(kept);
}

function concat(parts) {
    const out = new Uint8Array(parts.reduce((total, part) => total + part.length, 0));
    let offset = 0;

    for (const part of parts) {
        out.set(part, offset);
        offset += part.length;
    }

    return out;
}
